import { IHttpService, IPromise } from 'angular';

import { appStore } from '../../app/AppStore';
import { SessionSiteSelectors, SessionUserSelectors } from '../../app/selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../app/ServiceLocator';
import { CollectionMapper } from '../mappers/CollectionMapper';
import { DatasourceMapper } from '../mappers/DatasourceMapper';
import { FlowMapper } from '../mappers/FlowMapper';
import { MetricMapper } from '../mappers/MetricMapper';
import { ProjectMapper } from '../mappers/ProjectMapper';
import { PublishedConnectionMapper } from '../mappers/PublishedConnectionMapper';
import { ViewMapper } from '../mappers/ViewMapper';
import { WorkbookMapper } from '../mappers/WorkbookMapper';
import { OrderKeys } from '../models/OrderKeys';
import { IOrderClause } from '../ServerApi/types/IOrderClause';

// Change endpoint to absolute path if you want to run a mixed local scenario
// e.g. local - 'http://localhost:8080/content-exploration/content'
//      kube -  'content-exploration/content'
// $MARK-jhallmann-20190423: need a story for dev/production configuration
const CRASS_URI: string = 'content-exploration/content';

export class Search {
    private searchExpression: String;
    private filterExpression: String;
    private sortExpression: String;
    private pageIndex = 0;
    private pageSize = 10;

    public search(expression: String): Search {
        this.searchExpression = expression;
        return this;
    }

    public filter(expression: String): Search {
      this.filterExpression = expression;
      return this;
    }

    public sort(expression: String): Search {
        this.sortExpression = expression;
        return this;
    }

    public withOrder(clauses: Array<IOrderClause>): Search {
        if (clauses && clauses.length > 0) {
            let expressions: Array<string> = [];
            for(let clause of clauses) {
                // TODO: Add support for explicit relevance sort.
                if (clause.field === OrderKeys.relevance) {
                    continue;
                }

                expressions.push(`${clause.field} ${clause.ascending ? 'asc' : 'desc'}`);
            }
            this.sortExpression = expressions.join(',');
        } else {
            this.sortExpression = '';
        }
        return this;
    }

    public page(pageIndex: number): Search {
        this.pageIndex = pageIndex;
        return this;
    }

    public limit(pageSize: number): Search {
        this.pageSize = pageSize;
        return this;
    }

    public getCount(): IPromise<number> {
        return this.getResults().then(result => result.total);
    }

    public getResults(): IPromise<SearchResult> {
        const userId = SessionUserSelectors.sessionUserId(appStore.getState());
        const siteId = SessionSiteSelectors.siteId(appStore.getState());

        const http = ServiceLocator.get<IHttpService>('$http');

        const params = new Array<String>();
        if (this.searchExpression) {
            params.push('search=' + this.searchExpression);
        }
        if (this.filterExpression) {
          params.push('filter=' + this.filterExpression);
        }
        if (this.sortExpression) {
            params.push('sort=' + this.sortExpression);
        }
        params.push('page=' + this.pageIndex);
        params.push('limit=' + this.pageSize);
        params.push(`user=${userId}`);
        params.push(`site=${siteId}`);
        params.push(`admin=true`);


        const url = CRASS_URI + '?' + params.join('&');
        const requestConfig: ng.IRequestConfig = {
            method: 'GET',
            url
        };

        return http(requestConfig).then((response: any) => {
            // TODO: Map crass data into required fields for vizportalclient.
            return {
                ...response.data,
                items: response.data.items.map((item: any) => {
                    if(item.content.type==='workbook'){
                        return WorkbookMapper.mapFromCRASS(item.content);
                    } else if(item.content.type==='unifieddatasource'){
                        return DatasourceMapper.mapFromCRASS(item.content);
                    } else if(item.content.type==='view'){
                        return ViewMapper.mapFromCRASS(item.content);
                    } else if(item.content.type==='project'){
                        return ProjectMapper.mapFromCRASS(item.content);
                    } else if(item.content.type==='flow'){
                        return FlowMapper.mapFromCRASS(item.content);
                    } else if(item.content.type==='metric'){
                        return MetricMapper.mapFromCRASS(item.content);
                    } else if(item.content.type === 'collection') {
                        return CollectionMapper.mapFromCRASS(item.content);
                    } else if(item.content.type==='publishedConnection'){
                        return PublishedConnectionMapper.mapFromCRASS(item.content);
                    }
                    return this.mapDefault(item);
                })
            };
        });
    }

    private mapDefault(item: any){
        return {
            ...item.content,
            id: item.content.syncId,
            contentType: item.content.type,
            name: item.content.title,
            tags: [],
            owner: {
                id: 0,
                displayName: item.content.ownerName || '',
                username: item.content.ownerUserName || '',
                domainName: '',
                isHiddenInternalUser: false
            }
        };
    }
}

export type SearchResult = {
    pageIndex: number;
    size: number;
    total: number;
    items: ReadonlyArray<any>;
};
